import { BookOpen, BrickWall, History, LayoutTemplate, ShieldCheck, SquareTerminal } from 'lucide-react';
import {
  CanaryArt,
  CommandFanoutArt,
  HardeningArt,
  PlaybookArt,
  RunHistoryArt,
  TemplateArt,
} from '@/modules/marketing/AutomationArt';
import type { Article } from '@/modules/marketing/article-types';

/**
 * The automation articles: playbooks, security hardening, the command runner, templates, how a run
 * reaches many servers safely, and the run history.
 *
 * Every figure here is read from the code — the distributions and their minimum versions from the
 * playbooks, the batch limit from the run strategy, the preflight checks from the worker — so an
 * article can be corrected by reading one file rather than by asking somebody what the product does.
 *
 * ## One bold lead-in per paragraph
 *
 * The article page renders a leading `**phrase**` as emphasis and nothing after it, so a list of
 * named items is one paragraph per item. Two lead-ins in one paragraph printed the second pair of
 * asterisks on the public page.
 */

export const playbooks: Article = {
  slug: 'playbooks',
  category: 'features',
  icon: BookOpen,
  art: PlaybookArt,
  eyebrow: 'Automation',
  title: 'Linux Server Playbooks',
  seoTitle: 'Linux Server Automation Playbooks',
  metaDescription:
    'Automate updates, security hardening, disk cleanup and software installs across Ubuntu, Debian and RHEL-compatible servers, with preflight checks and dry runs.',
  standfirst:
    'Playbooks are tested procedures for everyday server work: updating packages, installing PHP, Python or Docker, hardening SSH, cleaning up disk space. Each one detects the distribution and package manager on the server and runs the right commands for it.',
  sections: [
    {
      heading: 'One playbook for every distribution',
      paragraphs: [
        'Installing PHP 8.4 takes different repositories and package names on Debian, on Ubuntu 22.04 and on Rocky Linux. A playbook detects the distribution, its version and the package manager installed on each server, and chooses the commands itself.',
        'You choose the playbook and its options, such as the PHP version and extensions. You do not need to know whether a server uses apt or dnf.',
      ],
    },
    {
      heading: 'Preflight checks before any change',
      paragraphs: [
        'Every run starts with a preflight on each server. It confirms the server is reachable, the account can act as root, the playbook supports the distribution and version, and, for anything that installs software, that at least 1 GB of disk space is free.',
        'A server that fails a check is left unchanged and the reason is recorded on the run, so an install never stops halfway through because of a problem that could have been found first.',
      ],
    },
    {
      heading: 'Root or sudo accounts',
      paragraphs: [
        'The platform does not assume it connects as root. It detects whether the account is root, has passwordless sudo, or needs a sudo password, and uses whichever applies. A stored sudo password is encrypted and passed to sudo on standard input, never on the command line.',
        'If an account cannot become root, the preflight says so and explains what to change.',
      ],
    },
    {
      heading: 'Safe to run repeatedly',
      paragraphs: [
        'Each step checks the current state before it acts. A package that is already installed is reported as installed, and a running service is left alone, so running a playbook again confirms the server is in the state you expect.',
        'The output separates what was already correct, what changed and, in a dry run, what would change.',
      ],
    },
    {
      heading: 'Dry runs and automatic retries',
      paragraphs: [
        'Every playbook supports a dry run, which performs the same checks and reports what it would do without changing anything.',
        'During a real run, temporary failures such as an unresponsive package mirror or a package lock held by unattended upgrades are retried automatically. Any other failure stops the step and shows the package manager’s output alongside the reason.',
      ],
    },
    {
      heading: 'Available playbooks',
      paragraphs: [
        '**Restart Services.** Restarts the services you choose, testing each one’s configuration first.',
        '**Update Packages.** Applies all updates or security updates only, without replacing configuration files that have been modified.',
        '**Clean Disk Space.** Removes downloaded package files, trims the journal and old rotated logs to limits you set, and can remove unused packages and untagged Docker images. It never touches /home, /var/www, /srv, /opt, databases or Docker volumes.',
        '**Advanced Security: Fail2Ban & Firewall Hardening.** Installs Fail2Ban for SSH, turns on ufw or firewalld with the ports you choose, and applies safer SSH settings, putting everything back if a check fails.',
        '**User Management.** Creates Linux users with SSH keys, grants or revokes sudo and group membership, lists who can already sign in, and locks, unlocks or removes accounts. Each run does one action on the users you name, so the history says exactly what was done to whom.',
        '**Runtimes and services.** Install Docker with the Compose plugin, Node.js 22 or 24 with global packages, PHP 8.2 to 8.5 with extensions and PHP-FPM, Python 3 with Gunicorn and uv, Poetry or Pipenv, Nginx, MariaDB, Redis and Composer, and a Prometheus, Node Exporter and Grafana stack in containers.',
        '**Web and certificates.** Put an Nginx reverse proxy with a Let’s Encrypt certificate in front of an app, API or container, turn on renewal that reloads nginx afterwards, and check renewal with a dry run.',
        '**CI runners.** Install the tools CI jobs expect and prepare GitHub Actions or GitLab runners, ready for you to register.',
        '**Kubernetes.** Install containerd, kubelet, kubeadm and kubectl on Ubuntu 22.04, Debian 12 or RHEL-compatible 9 and later, and either start a control plane with a pod network or prepare a worker to join one. The join command is yours to run: it carries a token, and a token has no business in a run log.',
        'Each playbook lists the distributions and minimum versions it supports.',
      ],
    },
  ],
  faq: [
    {
      q: 'Which Linux distributions do playbooks support?',
      a: 'Ubuntu 22.04 and later, Debian 12 and later, and RHEL-compatible 8 and later: AlmaLinux, Rocky Linux, RHEL, CentOS Stream and Oracle Linux. Most playbooks also support Amazon Linux 2023. The maintenance playbooks and some installs still accept older releases such as Ubuntu 20.04, Debian 11 and Amazon Linux 2, which are past the end of standard support. Each playbook lists exactly what it accepts.',
    },
    {
      q: 'Does the platform need to connect as root?',
      a: 'No. It works with root, with an account that has passwordless sudo, or with an account whose sudo password you store with its credential. The platform detects which applies when it checks the server.',
    },
    {
      q: 'What does a dry run do?',
      a: 'It connects, runs the preflight and walks through each step, reporting what is already in place and what would change. Nothing is installed, restarted or removed.',
    },
    {
      q: 'Will a playbook reboot my server?',
      a: 'No. If updates need a reboot to take effect, such as a new kernel, the run tells you. Rebooting is left to you.',
    },
    {
      q: 'Do I need to install an agent?',
      a: 'No. Playbooks run over the SSH connection configured for the server, and nothing is left running on the server afterwards.',
    },
  ],
  points: [
    'Detects distribution, version and package manager',
    'Preflight checks access, root, support and disk space',
    'Dry run for every playbook',
    'Safe to run repeatedly',
    'Temporary failures retried automatically',
  ],
  related: [
    { label: 'Security hardening', to: '/features/security-hardening' },
    { label: 'Server templates', to: '/features/server-templates' },
    { label: 'Safe rollouts', to: '/features/safe-rollouts' },
  ],
};

export const securityHardening: Article = {
  slug: 'security-hardening',
  category: 'features',
  icon: BrickWall,
  art: HardeningArt,
  eyebrow: 'Automation',
  title: 'Security Hardening',
  seoTitle: 'Fail2Ban, Firewall and SSH Hardening for Linux',
  metaDescription:
    'Install Fail2Ban, turn on ufw or firewalld and apply safer SSH settings on Ubuntu, Debian and RHEL-compatible servers without locking yourself out.',
  standfirst:
    'One playbook installs Fail2Ban for SSH, turns on the firewall your distribution uses and applies safer SSH settings. It is built around the one mistake nobody can fix remotely: cutting off SSH access to the server.',
  sections: [
    {
      heading: 'What the playbook changes',
      paragraphs: [
        '**Fail2Ban for SSH.** A jail that bans an address after repeated failed logins: five in ten minutes earns a one-hour ban by default, with a strict and a relaxed rule to choose from. The playbook proves the jail works by making one failed login itself and checking that the jail saw it.',
        '**A firewall.** ufw on Ubuntu and Debian, firewalld on the RHEL family and Amazon Linux, allowing SSH, the ports you list and, if you choose, every port a service already accepts outside connections on.',
        '**Safer SSH settings.** No empty passwords, at most four authentication attempts per connection, 60 seconds to sign in and no X11 forwarding. Turning off password logins or root logins is a separate choice you make explicitly.',
      ],
    },
    {
      heading: 'It checks the firewall you already have',
      paragraphs: [
        'A server that already filters traffic with csf, a hand-written nftables ruleset, saved iptables rules or rules no firewall manager owns keeps them. The run reports what it found and leaves the firewall alone.',
        'If ufw or firewalld is already on, the playbook adds its rules to it. It never removes a rule a firewall already has, and it never installs a second firewall beside the first.',
      ],
    },
    {
      heading: 'Your SSH access stays open',
      paragraphs: [
        'Before changing anything, the playbook finds the SSH connection it is running on and how that session signed in. Every port sshd listens on is allowed, and the address the platform connects from is never banned, along with any office or VPN addresses you list.',
        'Turning off password logins is refused while the platform signs in with a password, and restricting root logins is refused while it signs in as root. The refusal comes before any change, so the server is left exactly as it was.',
      ],
    },
    {
      heading: 'Undone automatically if anything fails',
      paragraphs: [
        'Before the first change, the playbook keeps a copy of every file it will replace and arms an undo on a systemd timer on the server. If a check fails, everything it changed is put back before the step ends.',
        'If the run is cut off halfway, for example by a dropped connection, the timer puts everything back within five minutes without the platform. The undo is disarmed only after sshd, the firewall and the Fail2Ban jail have all been checked and the original connection is still open.',
      ],
    },
    {
      heading: 'Start with a dry run',
      paragraphs: [
        'A dry run reports the firewall it found, the ports it would allow and, when it would turn a firewall on, the listening ports that would be blocked. Nothing is installed or changed.',
        'Firewalls outside the server, such as cloud security groups, are not changed. Ports Docker publishes stay reachable whatever the host firewall allows, and the run says so when a server runs Docker.',
      ],
    },
  ],
  faq: [
    {
      q: 'Can this lock me out of my server?',
      a: 'It is built not to. Every SSH port stays open, the platform’s own address is never banned, choices that would stop the current way of signing in are refused, and a run that fails or is cut off is undone. Firewalls outside the server, such as cloud security groups, are not touched, so check those separately.',
    },
    {
      q: 'Which distributions does it support?',
      a: 'Ubuntu 22.04 and later, Debian 12 and later, RHEL-compatible 8 and later (AlmaLinux, Rocky Linux, RHEL, CentOS Stream and Oracle Linux) and Amazon Linux 2023. Amazon does not package Fail2Ban for Amazon Linux 2023, so run it there with Fail2Ban turned off.',
    },
    {
      q: 'What if my server already uses csf, nftables or iptables?',
      a: 'The firewall is left exactly as it is and the run tells you what it found. The SSH settings can still be applied, and Fail2Ban is skipped where csf and lfd already ban failed logins.',
    },
    {
      q: 'How do I reverse the SSH settings later?',
      a: 'A later run never loosens SSH settings an earlier run applied. To return to your previous settings, delete /etc/ssh/sshd_config.d/00-iap-hardening.conf on the server and reload sshd. Packages the playbook installed stay installed.',
    },
    {
      q: 'Does Auto-Fix change firewall rules?',
      a: 'No. Auto-Fix never changes firewall rules, reboots a server or deletes data. Firewall changes happen only when you run this playbook.',
    },
  ],
  points: [
    'Fail2Ban jail for SSH, tested with a real failed login',
    'ufw or firewalld, or an existing firewall left alone',
    'Every SSH port and the platform’s address kept open',
    'Put back automatically if a check fails or the run is cut off',
    'Dry run shows what would be blocked',
  ],
  related: [
    { label: 'Linux server playbooks', to: '/features/playbooks' },
    { label: 'Safe rollouts', to: '/features/safe-rollouts' },
  ],
};

export const commandRunner: Article = {
  slug: 'command-runner',
  category: 'features',
  icon: SquareTerminal,
  art: CommandFanoutArt,
  eyebrow: 'Automation',
  title: 'Command Runner',
  seoTitle: 'Run Commands on Multiple Linux Servers',
  metaDescription:
    'Run a shell command across selected servers, groups, tags or operating systems. See live output from each server, retry failures and keep a full history.',
  standfirst:
    'Run a shell command on the servers you choose, by name, group, tag or operating system, and follow the output from each one as it arrives. Every command is recorded with who ran it and what each server returned.',
  sections: [
    {
      heading: 'Non-interactive by design',
      paragraphs: [
        'There is no terminal session and no prompt. Each command runs to completion exactly as typed, and its output, exit code and duration are recorded. A command that waits for input receives none and exits.',
        'That is what makes it practical to run a command across many servers, and what makes the record of it reliable.',
      ],
    },
    {
      heading: 'Choose servers by name, group, tag or OS',
      paragraphs: [
        'Target servers by name, group, tag or operating system, or select every server on the account. Before anything runs, your selection is resolved into a list of servers, with known problems flagged: never connected, unreachable at the last check, or unable to become root.',
        'The list is only a preview. No server is contacted until you start the run, and each server is checked again when it does.',
      ],
    },
    {
      heading: 'Live output from every server',
      paragraphs: [
        'Each server has its own status and log, updated while the command runs. Open any server to read its output, or download the log as a text file.',
        'Every command has a time limit between one minute and one hour. A command still running at the limit is stopped on the server itself, so it cannot keep running after you close the page.',
      ],
    },
    {
      heading: 'Retry only the servers that failed',
      paragraphs: [
        'If a command fails on three of forty servers, a retry starts a new run on those three, along with any servers that were blocked, skipped or cancelled. The new run links back to the original, and servers that succeeded are not touched again.',
        'Cancelling stops the command on servers where it is still running and skips servers that have not started. Both actions are recorded on the run.',
      ],
    },
    {
      heading: 'Run as root or as the connecting account',
      paragraphs: [
        'Run a command as root, directly or through sudo, or as the account the platform connects with, for servers where that account does not need root.',
        'Commands and their output are kept in the run history, where everyone on your account can read them. Do not include passwords, tokens or keys in a command.',
      ],
    },
  ],
  faq: [
    {
      q: 'Is this a web-based SSH terminal?',
      a: 'No. There is no interactive session. Each command runs to completion on every selected server, and the result is recorded as a run.',
    },
    {
      q: 'Can I run a command only on certain operating systems?',
      a: 'Yes. Select servers by operating system, for example every Debian and Ubuntu server, or by the groups and tags you already use.',
    },
    {
      q: 'What happens if a server is unreachable?',
      a: 'The preflight marks it as blocked with the reason, nothing runs on it, and the other servers continue. Connection failures are retried a few times before a server is marked as blocked.',
    },
    {
      q: 'Can I stop a command that is taking too long?',
      a: 'Yes. Cancel stops it on servers where it is still running. Every command also has a time limit between one minute and one hour, enforced on the server.',
    },
    {
      q: 'Who can see the commands I run?',
      a: 'Everyone on your account can read the run history, including each command and its output. Commands are never shared outside your account.',
    },
  ],
  points: [
    'Target by name, group, tag, OS or all servers',
    'Live status and output for each server',
    'Retry failed servers as a linked run',
    'Cancel at any time; time limits enforced on the server',
    'Every command kept in the run history',
  ],
  related: [
    { label: 'Safe rollouts', to: '/features/safe-rollouts' },
    { label: 'Run history', to: '/features/run-history' },
  ],
};

export const serverTemplates: Article = {
  slug: 'server-templates',
  category: 'features',
  icon: LayoutTemplate,
  art: TemplateArt,
  eyebrow: 'Automation',
  title: 'Server Templates',
  seoTitle: 'Server Setup Templates for Web, Python and CI',
  metaDescription:
    'Prepare WordPress, Laravel, Node.js or Python servers, an Nginx reverse proxy with Let’s Encrypt, or a CI runner host in one run from tested playbooks.',
  standfirst:
    'A template is a versioned sequence of playbooks that prepares a server for a specific role. Choose the few options that differ, such as the PHP version, the Python tools or the domains to serve, and run it on one server or many.',
  sections: [
    {
      heading: 'Nine templates',
      paragraphs: [
        '**WordPress Server.** Nginx, PHP with the extensions WordPress requires, and MariaDB.',
        '**Laravel Server.** Nginx, PHP with the extensions Laravel requires, Composer and Node.js, with optional MariaDB and Redis.',
        '**Node.js Server.** Node.js from nodejs.org, PM2, and Nginx in front.',
        '**Python Production Stack.** Python, Nginx, Gunicorn in the application’s own virtual environment, uv, Poetry or Pipenv, and a service account with a hardened systemd service for a FastAPI or Django app.',
        '**Nginx Reverse Proxy + Auto-Renewing SSL.** Nginx in front of an app, API or container, a Let’s Encrypt certificate requested only after a dry run against the staging environment, renewal that reloads nginx, and a final check that renewal works.',
        '**CI/CD Self-Hosted Runner Host.** Docker, the tools CI jobs expect, and GitHub Actions or GitLab runner accounts and files, with Fail2Ban and a firewall that allows only SSH by default.',
        '**Docker Host.** Docker Engine from Docker’s own repositories, with the Compose plugin.',
        '**Monitoring Server.** Prometheus, Node Exporter and Grafana in containers, reachable only from the server itself.',
        '**Kubernetes Node Setup.** Containerd with systemd cgroups, the kernel modules and sysctl settings kubelet needs, then a control plane with a pod network or a worker prepared to join one.',
      ],
    },
    {
      heading: 'Steps run in order',
      paragraphs: [
        'Each step is a standard playbook with the same preflight, state checks and output. Steps run in order on each server. If a step fails, the remaining steps are skipped on that server and the run shows which step failed.',
        'Every step checks that it supports the server before the run changes anything, so a template aimed at an unsupported distribution stops at the preflight.',
      ],
    },
    {
      heading: 'Versioned templates',
      paragraphs: [
        'Each template has a version and change notes. A run records the template version and the values used for every step.',
        'When you review a run later, it shows what actually ran on that day, not what the current version of the template contains.',
      ],
    },
    {
      heading: 'What templates do not do',
      paragraphs: [
        'A template prepares the server. It does not deploy your application, create its databases or change your DNS. The Python Production Stack installs the service for your app and leaves it stopped until your code is in place, and the run ends with the steps that remain.',
        'The reverse proxy template needs your domains to point at the server and ports 80 and 443 to be open before it runs. The CI runner template never registers a runner, because that takes a token the platform has no reason to hold; the run ends with the command to register on the server.',
        'Because steps check before they act, running a template on a server that is already partly set up is safe. Completed steps are reported as already done.',
      ],
    },
  ],
  faq: [
    {
      q: 'Does a template deploy my application?',
      a: 'No. The WordPress Server template prepares the server WordPress runs on, and the Python Production Stack sets up the service your app will run as without starting it. Deploying the code and configuring the site are up to you.',
    },
    {
      q: 'Can I dry run a template?',
      a: 'Yes. A dry run walks through every step on every selected server and reports what would change, without changing anything.',
    },
    {
      q: 'What happens if a step fails?',
      a: 'The remaining steps are skipped on that server, and the run shows the failed step and its output. Other servers continue according to the run strategy.',
    },
    {
      q: 'Can I run a template on a server that is already set up?',
      a: 'Yes. Each step checks the current state first, so anything already installed is reported as installed rather than installed again.',
    },
    {
      q: 'Which distributions do templates support?',
      a: 'Whatever their steps support. The Python, reverse proxy and CI runner templates start at Ubuntu 22.04 and Debian 12, templates that install PHP do not run on Amazon Linux, and the preflight reports any step that does not support a server.',
    },
  ],
  points: [
    'WordPress, Laravel, Node.js and Python app servers',
    'Reverse proxy with Let’s Encrypt, and CI runner hosts',
    'Steps run in order; a failure stops that server',
    'Template version recorded on every run',
    'Dry runs, canaries and batches like any other run',
  ],
  related: [
    { label: 'Linux server playbooks', to: '/features/playbooks' },
    { label: 'Security hardening', to: '/features/security-hardening' },
  ],
};

export const safeRollouts: Article = {
  slug: 'safe-rollouts',
  category: 'features',
  icon: ShieldCheck,
  art: CanaryArt,
  eyebrow: 'Automation',
  title: 'Safe Rollouts',
  seoTitle: 'Safe Rollouts: Preflight, Canary and Batches',
  metaDescription:
    'Roll out server changes safely: preflight checks on every server, a canary server confirmed by a person, then batches that stop when failures pass your limit.',
  standfirst:
    'Every playbook, template and command run follows the same sequence: preflight checks, an optional dry run, one canary server, your confirmation, then batches. A problem shows up on one server instead of all of them.',
  sections: [
    {
      heading: 'Preflight checks on every server',
      paragraphs: [
        'Before any change, each selected server is checked: reachable, able to act as root when the run needs it, supported by the playbook, and with enough free disk space for installs. Servers that fail are marked as blocked with the reason and left untouched.',
        'Preflight checks run on several servers at a time rather than one after another.',
      ],
    },
    {
      heading: 'A canary server first',
      paragraphs: [
        'With the canary on, one server runs the change by itself first. If it fails, the remaining servers are skipped and the run stops. If it succeeds, the run pauses until someone confirms, with the canary server’s output available to review.',
        'Dry runs skip the canary, because they change nothing.',
      ],
    },
    {
      heading: 'Batches with a failure limit',
      paragraphs: [
        'After confirmation, the remaining servers run in batches of 1 to 50 servers at a time, until every server in the run is done. Each batch starts only when the previous one has finished. The batch size limits how many servers change at once, not how many a run can include.',
        'You set how many failures to tolerate. The default is zero: once failures exceed the limit, no further batches start and the remaining servers are left unchanged.',
      ],
    },
    {
      heading: 'Retry failed servers',
      paragraphs: [
        'When a run finishes, every server has a status: succeeded, failed, blocked, skipped or cancelled. A retry starts a new run for every server that did not succeed, linked to the original, so servers that succeeded are never run twice.',
      ],
    },
    {
      heading: 'Example: updating 50 servers',
      paragraphs: [
        '**Preflight.** All 50 servers are checked. Two are out of disk space and marked as blocked.',
        '**Canary.** One of the remaining 48 servers is updated successfully.',
        '**Confirmation.** You review the canary server’s output and continue.',
        '**Batches.** The remaining servers update in batches of five. One fails after a package mirror times out twice.',
        '**Retry.** After freeing disk space on the two blocked servers, you retry every server that did not succeed.',
      ],
    },
  ],
  faq: [
    {
      q: 'Can I turn off the canary?',
      a: 'Yes, for runs where waiting for confirmation is not useful. It is on by default, and it matters most for commands, which have no dry run.',
    },
    {
      q: 'How long does a run wait for confirmation?',
      a: 'Up to 24 hours after the canary succeeds. Nothing else runs in the meantime, and if nobody confirms, the run is cancelled and the other servers are not touched.',
    },
    {
      q: 'What does a failure limit of zero mean?',
      a: 'The first failure after the canary stops further batches from starting. Servers already running in the current batch finish, and the rest are left untouched.',
    },
    {
      q: 'How large can a batch be?',
      a: 'Between 1 and 50 servers at a time. A run itself can include every server on your account, as many as your plan allows; the batches work through them in turn.',
    },
    {
      q: 'Does the preflight change anything on the server?',
      a: 'No. It connects and reads what the server runs and what the account can do. A server that fails the preflight is left exactly as it was.',
    },
  ],
  points: [
    'Preflight checks on every server before any change',
    'One canary server, then a person confirms',
    'Batches of 1 to 50 at a time',
    'Stops when failures exceed your limit',
    'Retry only the servers that did not succeed',
  ],
  related: [
    { label: 'Linux server playbooks', to: '/features/playbooks' },
    { label: 'Run history', to: '/features/run-history' },
  ],
};

export const runHistory: Article = {
  slug: 'run-history',
  category: 'features',
  icon: History,
  art: RunHistoryArt,
  eyebrow: 'Automation',
  title: 'Run History',
  seoTitle: 'Run History and Audit Trail for Server Changes',
  metaDescription:
    'Every playbook, template and command run is recorded: who started it, the parameters, the servers, each server’s output and exit code, and the final result.',
  standfirst:
    'Every change made through automation is recorded as a run. The run history is your audit trail: who started it, with which parameters, on which servers, what each server returned and how it ended.',
  sections: [
    {
      heading: 'What each run records',
      paragraphs: [
        'The playbook, template or command, the values used, whether it was a dry run, the rollout strategy and the person who started it.',
        'For each server: the preflight results, each step with its outcome and attempts, the full output in the order it was printed, the exit code and the duration.',
      ],
    },
    {
      heading: 'Follow a run while it happens',
      paragraphs: [
        'An open run refreshes every second: each server’s status, the step it has reached and the output it has printed so far. Only output that has not been shown yet is fetched, so a long log does not slow the page down.',
        'A run waiting for someone to confirm its canary server says so at the top, with the canary’s output available to read before the rest of the servers start.',
      ],
    },
    {
      heading: 'Runs are never edited',
      paragraphs: [
        'Runs are not changed after they finish. Retrying failed servers creates a new run linked to the original, so the first run still shows exactly what happened, and the retry shows what happened the second time.',
        'Running a command again from its history fills in the same command, account and time limit, but not the servers. Those are chosen again on purpose, so a command is never repeated on a fleet by accident.',
      ],
    },
    {
      heading: 'Search and filter runs',
      paragraphs: [
        'Filter runs by type (playbook, template or command), by status, by playbook or by server. Filtering by server shows everything that has been run on that machine.',
        'Open a run to read any server’s output, or download a server’s log as a text file.',
      ],
    },
    {
      heading: 'Accurate status for every server',
      paragraphs: [
        'A run is marked as succeeded only if it succeeded on every server it targeted. A run that worked on some servers and not others is marked as partially failed, with the servers grouped by outcome.',
        'A server that was never changed says why: blocked by the preflight, skipped after an earlier failure stopped further batches, or cancelled. The difference matters when you decide what to retry.',
      ],
    },
    {
      heading: 'Private to your account',
      paragraphs: [
        'Everyone on your account can read every run, including commands and their output, which is why commands should never contain passwords or tokens. Nobody outside your account can open one of your runs, even with its address.',
      ],
    },
  ],
  faq: [
    {
      q: 'Are dry runs recorded?',
      a: 'Yes. A dry run is recorded like any other run, marked as a dry run, with what it found on each server.',
    },
    {
      q: 'Can I see everything that was run on one server?',
      a: 'Yes. Filter the run history by server, or open it from that server’s page.',
    },
    {
      q: 'Can a run be edited or deleted?',
      a: 'No. A retry creates a new, linked run, and the original stays as it was.',
    },
    {
      q: 'Can I download the output?',
      a: 'Yes. Each server’s log in a run can be downloaded as a plain text file.',
    },
    {
      q: 'Does the history show who ran something?',
      a: 'Yes. Every run records the person on your account who started it, and confirmations are recorded the same way.',
    },
  ],
  points: [
    'Who, what, parameters and servers for every run',
    'Per-server checks, steps, output, exit code and duration',
    'Retries create linked runs; nothing is overwritten',
    'Filter by type, status, playbook or server',
    'Download any server’s log',
  ],
  related: [
    { label: 'Command runner', to: '/features/command-runner' },
    { label: 'Safe rollouts', to: '/features/safe-rollouts' },
  ],
};
